import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography, Container, Paper, FormHelperText } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import Footer from '../home/Footer';
import './Register.css';
import { RegisterModel } from "@ontime/types";
import AuthService from '../services/AuthService';
import CustomFormAction from '../customComponents/CustomFormAction';
import CustomButton from '../customComponents/CustomButton';

const registerSchema = z.object({
    emailAddress: z.string().email("Invalid email format"),
    password: z.string()
        .min(8, "Password must be at least 8 characters")
        .regex(/^(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{8,}$/,
            "Password must contain at least one uppercase letter, one number, and one special character"),
    confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
    path: ["ConfirmPassword"],
    message: "Passwords do not match",
});

const Register: React.FC = () => {
    const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<RegisterModel>({
        resolver: zodResolver(registerSchema),
        defaultValues: {
        }
    });

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!AuthService.isTokenExpired()) {
            navigate('/')
        }
        if (AuthService.isTokenExpired()) {
            AuthService.logout();
        }
    });

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const handleRegister = async (data: RegisterModel) => {

        setLoading(true);

        try {
            const response = await AuthService.register(data);

            console.log('response', response.success);

            if (response.success) {
                enqueueSnackbar('Registration successful! Please check your email for verification.', { variant: 'success' });
                navigate('/confirmregistration');
            }
        } catch (error) {

            let errorMessage = typeof error === 'string' ? error : 'Registration failed. Please try again.';
            console.log('errrrrr',error)
            if (errorMessage === 'User already exists') {
                navigate('/login');
                enqueueSnackbar(`${errorMessage} , Please login!`, { variant: 'error' });
            }
            else {
                enqueueSnackbar(errorMessage, { variant: 'error' });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="register-container">
            <Container component="main" maxWidth="xs">
                <Paper className="register-paper" elevation={3}>
                    <Box className="logo-box" >
                        <img onClick={() => navigate('/home')} style={{ cursor: 'pointer' }} src="logo.png" alt="Logo" className="register-logo" />
                    </Box>
                    <Typography component="h1" variant="h5" className="register-title">
                        Register to System
                    </Typography>
                    <form onSubmit={handleSubmit(handleRegister)} noValidate>

                        <Box mt={2}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label="Email"
                                InputLabelProps={{ shrink: true }}
                                {...register('emailAddress')}
                                error={!!errors.emailAddress}
                                helperText={errors.emailAddress?.message}
                            />
                        </Box>

                        <Box mt={2}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label="Password"
                                InputLabelProps={{ shrink: true }}
                                type="password"
                                {...register('password')}
                                error={!!errors.password}
                                helperText={errors.password?.message}
                            />
                        </Box>

                        <Box mt={2}>
                            <TextField
                                variant="outlined"
                                fullWidth
                                label="Confirm Password"
                                InputLabelProps={{ shrink: true }}
                                type="password"
                                {...register('confirmPassword')}
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword?.message}
                            />
                        </Box>

                        {watch('password') !== watch('confirmPassword') && <FormHelperText error sx={{ ml: 2 }}>Inputed passwords are not match</FormHelperText>}
                        <CustomFormAction>
                            <CustomButton
                                lable={loading ? 'Registering ...' : 'Register'}
                                type='submit'
                                disabled={loading}
                                infoContent={`Register in application`}
                                size='medium'
                            />
                        </CustomFormAction>

                        <Box mt={2} textAlign="center">
                            <Typography variant="body2">
                                Already have an account? <a href="/login" style={{ textDecoration: "none", color: "#1976d2" }}>Log in here</a>
                            </Typography>
                        </Box>
                    </form>

                </Paper>
            </Container>
            <Box sx={{ height: '65px' }} />
            <div className="footer-register-container" >
                <Footer />
            </div>
        </div>
    );
};

export default Register;
